import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ZiroFileUploadConfig } from '../../../forms/fields/file-upload/ziro-file-upload.component';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../../../shared/services/toast.service';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import { CdrDumpFileImportResource } from '../../resources/cdr-dump-file-import.resource';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { EMPTY, Observable, Subscription, throwError } from 'rxjs';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { JobState, JobStatus } from '../../../shared/models/generated/ziroModels';
import { CdrDumpFileImportPollingContext } from '../../contexts/cdr-dump-file-import-polling.context';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { catchError, map, tap } from 'rxjs/operators';
import { CdrValidationService } from '../../../shared/services/cdr-validation.service';

export interface ManualCdrImport {
  cdrTextFile: File;
}

@Component({
  selector: 'ziro-admin-manual-cdr-import',
  templateUrl: './cdr-dump-file-import.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [CdrDumpFileImportResource, CdrDumpFileImportPollingContext, CdrValidationService],
  standalone: false,
})
export class CdrDumpFileImportComponent
  extends ZiroFormAbstractDirective<ManualCdrImport>
  implements OnInit, OnDestroy
{
  private bottomNavService = inject(BottomNavService);
  private toastService = inject(ToastService);
  private manualImportCdrResource = inject(CdrDumpFileImportResource);
  protected _ziroFormStateService: ZiroFormStateService;
  private manualCdrImportPollingContext = inject(CdrDumpFileImportPollingContext);
  private breadcrumbsService = inject(BreadcrumbsService);
  private cdrValidationService = inject(CdrValidationService);

  isLoading = true;
  bottomNavId = 'manualCdrImportButton';
  bottomNavButtons: BottomNavButton;
  bottomNavImportLabel = 'tkey;admin.manual_cdr_import.button.import';
  bottomNavUpdatingLabel = 'tkey;admin.manual_cdr_import.button.in_progress';
  previousJobState: JobState;
  currentJobState: JobState;
  jobIsCurrentlyRunning = false;
  isSuccess: boolean;
  ziroIcons = ZiroIcons;

  formConfig = {
    fields: {
      cdrTextFile: {
        required: true,
        label: 'tkey;admin.manual_cdr_import.input.label',
        dataAutomation: 'manual-cdr-import-file-field',
        componentConfig: new ZiroFileUploadConfig({
          acceptedFileExtensions: { acceptedExtensions: '.txt' },
          maxKbSize: 3145728,
        }),
        validation: [
          {
            validator: (data) =>
              this.cdrValidationService
                .validateCdrFile(data)
                .pipe(map((isValid) => (isValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID))),
            message: 'tkey;admin.manual_cdr_import.csv.error',
          },
        ],
        debounceTime: 0,
      },
    },
  } as ZiroFormConfig;

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.manual_cdr_import.title' }]);
    this.bottomNavButtons = {
      id: this.bottomNavId,
      label: this.bottomNavImportLabel,
      icon: ZiroIcons.IMPORT,
      buttonClass: ButtonStyles.PRIMARY,
      dataAutomation: 'bottom-nav-import-button',
      submitSubject: this._validateAndSubmitSource,
      type: ButtonTypes.SUBMIT,
      state: {
        buttonDisableState: {
          disabled: false,
          pending: false,
          tooltipKey: '',
        },
      },
    } as BottomNavButton;

    this.bottomNavService.dispatch(new BottomNavUpdateButtonsList([this.bottomNavButtons]));
    this._listenToPolling();
    const validateAndSubmitSub = this._validateAndSubmitSource.subscribe(() => {
      this.bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: !this.isFormValid(),
        }),
      );
    });
    this._subscriptions.add(validateAndSubmitSub);
    this.isLoading = false;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.breadcrumbsService.clearBreadcrumbs();
    this.manualCdrImportPollingContext.stopPolling();
    this._subscriptions.unsubscribe();
  }

  protected submit(): Observable<void> {
    return this._postCdrImportTextFileAsBinary();
  }

  private _listenToPolling(): void {
    this.manualCdrImportPollingContext.startPolling();
    this.manualCdrImportPollingContext.state$.subscribe(this._handlePolling);
  }

  private _handlePolling = (jobStatus: JobStatus): void => {
    this.previousJobState = this.currentJobState || jobStatus.jobState;
    this.currentJobState = jobStatus.jobState;
    this.isSuccess = this.currentJobState === JobState.IDLE && this.previousJobState !== JobState.IDLE;
    if (this.isSuccess) {
      this.jobIsCurrentlyRunning = false;
      this._ziroFormStateService.setIsFormDirty(false);
      this._updateBottomNavButtonAsValid();
      this.previousJobState = JobState.IDLE;
      this.currentJobState = JobState.IDLE;
      this.manualCdrImportPollingContext.stopPolling();
      this.toastService.push(
        ToastTypes.SUCCESS,
        ZiroIcons.IMPORT,
        'tkey;admin.manual_cdr_import.toast.success.title',
        'tkey;admin.manual_cdr_import.toast.success.description',
      );
    }
    if (
      jobStatus.error !== null &&
      (this.currentJobState === JobState.IDLE || this.previousJobState === JobState.RUNNING) &&
      this.previousJobState !== JobState.IDLE
    ) {
      this.jobIsCurrentlyRunning = false;
      this._ziroFormStateService.setIsFormDirty(false);
      this._updateBottomNavButtonAsValid();
      this.manualCdrImportPollingContext.stopPolling();
      this.toastService.push(
        ToastTypes.ERROR,
        ZiroIcons.PHONE_LIST,
        'tkey;admin.manual_cdr_import.failure.toast.title',
        'tkey;admin.manual_cdr_import.failure.toast.message',
      );
    }

    if (this.currentJobState === JobState.RUNNING) {
      this.jobIsCurrentlyRunning = true;
      this._updateBottomNavButtonAsPending();
    }
  };

  private _updateBottomNavButtonAsValid(): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          ...this.bottomNavButtons,
          state: { buttonDisableState: { disabled: false, tooltipKey: '' }, pending: false },
          label: this.bottomNavImportLabel,
        },
      ]),
    );
  }

  private _updateBottomNavButtonAsPending(): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          ...this.bottomNavButtons,
          state: { buttonDisableState: { disabled: true, tooltipKey: '' }, pending: true },
          label: this.bottomNavUpdatingLabel,
        },
      ]),
    );
  }

  private _postCdrImportTextFileAsBinary(): Observable<void> {
    this._updateBottomNavButtonAsPending();
    this.jobIsCurrentlyRunning = true;

    return this.manualImportCdrResource.post(this.formData.cdrTextFile).pipe(
      tap(() => {
        this.currentJobState = JobState.RUNNING;
        this._listenToPolling();
      }),
      catchError(() => {
        this.jobIsCurrentlyRunning = false;
        this._updateBottomNavButtonAsValid();
        this.toastService.push(
          ToastTypes.ERROR,
          ZiroIcons.IMPORT,
          'tkey;admin.manual_cdr_import.toast.invalid.title',
          'tkey;admin.manual_cdr_import.toast.invalid.description',
        );
        return throwError(() => EMPTY);
      }),
    );
  }
}
